import React, { useEffect, useRef, useState } from 'react';
import { Button } from '@mui/material';
import { Confirm, useDelete, useListContext, useNotify, useTranslate } from 'react-admin';
import DeleteIcon from '@/assets/icons/delete-icon.svg?react';
import { usePopoverContext } from '@/components/global/buttons/CustomMorePopover';
import { CustomConfirmContent } from '@/components/global/buttons/CustomConfirmContent.jsx';
import { globalStyles } from '@/themes/commonStyles/index.js';
import PropTypes from 'prop-types';

const CustomDeleteButton = ({ resource, id, label, confirmContent, disabled = false, onSuccess }) => {
    const { closePopover } = usePopoverContext();
    const translate = useTranslate();
    const buttonLabel = label || translate('common.remove');
    const content = confirmContent || translate('ra.message.delete_content');

    const [open, setOpen] = useState(false);
    const notify = useNotify();
    const { refetch } = useListContext();
    const isMounted = useRef(true);

    const [deleteOne, { isLoading }] = useDelete(
        resource,
        { id },
        {
            onSuccess: () => {
                notify(translate('common.operations.deleted'));
                onSuccess?.();
            },
            onError: error => {
                notify(error.message, { type: 'error' });
            },
            onSettled: () => {
                if (isMounted.current) {
                    setOpen(false);
                }
                refetch();
            },
        },
    );

    useEffect(() => {
        isMounted.current = true;
        return () => {
            isMounted.current = false;
        };
    }, []);

    const handleClick = () => setOpen(true);
    const handleDialogClose = () => {
        setOpen(false);
        closePopover();
    };
    const handleConfirm = () => {
        deleteOne();
        closePopover();
    };

    return (
        <>
            <Button onClick={handleClick} disabled={disabled} aria-label={buttonLabel} data-keep-popover-open={true}>
                <DeleteIcon /> {buttonLabel}
            </Button>
            <Confirm
                isOpen={open}
                loading={isLoading}
                content={<CustomConfirmContent content={content} handleDialogClose={handleDialogClose} handleConfirm={handleConfirm} loading={isLoading} />}
                onConfirm={handleConfirm}
                onClose={handleDialogClose}
                sx={{ ...globalStyles.customConfirmDialog }}
            />
        </>
    );
};

export default CustomDeleteButton;

CustomDeleteButton.propTypes = {
    resource: PropTypes.string.isRequired,
    id: PropTypes.string.isRequired,
    label: PropTypes.string,
    confirmContent: PropTypes.string,
    disabled: PropTypes.bool,
    onSuccess: PropTypes.func,
};
